import { FC, useState } from "react";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { DragNDropFile } from "components/DragNDropFile";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import CustomTable from "components/Table/CustomTable";
import { ParsedRequirementRow } from "../parseRequirementsCsv";
import { RequirementImportType, ResolvedCompetencyLink } from "../types";
import { REQUIREMENT } from "helpers/constants";
import { CompetencyLinksList } from "../components/CompetencyLinksList";
import { CsvFormatInfoDialog } from "../components/CsvFormatInfoDialog";

type Props = {
  requirementType: RequirementImportType;
  requirements: ParsedRequirementRow[];
  resolveCompetencyLinks: (names: string[]) => ResolvedCompetencyLink[];
  error: string | null;
  onDownloadTemplate: () => void;
  onFileUploaded: (file: File) => void;
  onNext: () => void;
  onCancel: () => void;
};

export const UploadStep: FC<Props> = ({
  requirementType,
  requirements,
  resolveCompetencyLinks,
  error,
  onDownloadTemplate,
  onFileUploaded,
  onNext,
  onCancel,
}) => {
  const [formatInfoOpen, setFormatInfoOpen] = useState(false);
  const hasData = requirements.length > 0;
  const isEpaImport = requirementType === REQUIREMENT.REQUIREMENT_EPA;
  const isIterImport = requirementType === REQUIREMENT.REQUIREMENT_ITER;
  const linksCompetenciesFromCsv = isEpaImport || isIterImport;
  const rowTypeLabel = isEpaImport ? "EPA" : isIterImport ? "ITER" : "row";

  const formatGuideLabel =
    requirementType === REQUIREMENT.REQUIREMENT_EPA
      ? "View EPA CSV column requirements"
      : requirementType === REQUIREMENT.REQUIREMENT_ITER
        ? "View ITER CSV column requirements"
        : "View competency CSV column requirements";

  const previewColumns = [
    {
      id: "name",
      label: isEpaImport ? "EPA Name" : isIterImport ? "ITER Name" : "Name",
      value: "name" as const,
    },
    {
      id: "description",
      label: "Description",
      value: (row: ParsedRequirementRow) =>
        row.description?.length > 80
          ? `${row.description.slice(0, 80)}...`
          : row.description,
    },
    ...(linksCompetenciesFromCsv
      ? [
          {
            id: "competencies",
            label: "Competencies",
            value: (row: ParsedRequirementRow) => row.competencies.length,
          },
        ]
      : []),
    ...(requirementType === REQUIREMENT.REQUIREMENT_COMPETENCY
      ? [
          {
            id: "rubrics",
            label: "Rubrics",
            value: (row: ParsedRequirementRow) => row.rubrics.length,
          },
        ]
      : []),
  ];

  return (
    <StepLayoutContainer
      title="Upload requirements CSV"
      description="Download the template, fill in your requirements, and upload the file."
      onClickNext={hasData ? onNext : undefined}
      onClickBack={onCancel}
      backButtonText="Cancel"
      nextButtonText="Continue"
      renderActionButtons={
        !hasData ? undefined : (
          <Button variant="contained" color="primary" onClick={onNext} fullWidth>
            Continue
          </Button>
        )
      }
    >
      {error && (
        <Typography color="error" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box mt={2}>
        <DragNDropFile
          maxFileSizeMB={5}
          allowedExtensions={["csv"]}
          onFileUploaded={onFileUploaded}
        />
      </Box>

      <Box mt={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Link
            component="button"
            variant="body2"
            onClick={() => setFormatInfoOpen(true)}
            sx={{ cursor: "pointer", textAlign: "left" }}
          >
            {formatGuideLabel}
          </Link>
          <Button
            variant="text"
            color="primary"
            onClick={onDownloadTemplate}
            startIcon={<FileDownload />}
            size="small"
          >
            Download Template
          </Button>
        </Stack>
      </Box>

      <CsvFormatInfoDialog
        open={formatInfoOpen}
        requirementType={requirementType}
        onClose={() => setFormatInfoOpen(false)}
        onDownloadTemplate={onDownloadTemplate}
      />

      {hasData && (
        <Box mt={3}>
          <Typography variant="h6" mb={1}>
            Preview ({requirements.length} {rowTypeLabel}(s))
          </Typography>
          <CustomTable
            entities={requirements}
            columns={previewColumns}
            handleActionClick={() => {}}
            fieldToFilter={(row: ParsedRequirementRow, filterText: string) =>
              row.name.toLowerCase().includes(filterText.toLowerCase())
            }
            isSelectable={false}
            extraActionItems={[]}
            idColumn="row_key"
          />

          {linksCompetenciesFromCsv && (
            <Box mt={3} pb={3}>
              <Typography variant="subtitle1" gutterBottom>
                Competencies to link
              </Typography>
              <Stack spacing={2}>
                {requirements.map((row) => (
                  <Box key={row.row_key}>
                    <Typography variant="subtitle2" gutterBottom>
                      {row.name}
                    </Typography>
                    <CompetencyLinksList
                      links={resolveCompetencyLinks(row.competencies)}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </StepLayoutContainer>
  );
};
